import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '@medflex/components/stories/Modal';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Logger from '@medflex/utils/Logger';
import InputField from '@medflex/components/stories/InputField';
import InputTextarea from '@medflex/components/stories/InputTextarea';
import Button from '@medflex/components/stories/Button';
import Spinner from '@medflex/components/stories/Spinner';
import { showSuccessToast, showAlertToast } from '@medflex/components/stories/Toast';
import { trackEvent } from '@medflex/utils/Tracking';
import { WEEKDAYS } from '@medflex/utils/constants';
import useUserProfile from '../hooks/useUserProfile';
import useModal from '../hooks/useModal';
import { MODALS } from '../hooks/useIsModal';

const validationSchema = yup.object().shape({
  additionalInformation: yup.string(),
  slots: yup.object({
    monday: yup.array(yup.string()),
    tuesday: yup.array(yup.string()),
    wednesday: yup.array(yup.string()),
    thursday: yup.array(yup.string()),
    friday: yup.array(yup.string()),
    saturday: yup.array(yup.string()),
    sunday: yup.array(yup.string()),
  }),
});

const OpeningHoursEditModal = () => {
  const { close } = useModal();
  const dispatch = useDispatch();
  const { userProfile, editContactPage, isUpdating } = useUserProfile();
  const {
    register: registerFormElement,
    handleSubmit: wrapSubmit,
    formState,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });
  const { t } = useTranslation('physician');
  const userProfileErrors = useSelector(e => e.errors.userProfile);
  const { additionalInformation, slots } = userProfile.attributes.pages.openingHours ?? {};
  const [information, setInformation] = useState(additionalInformation || '');

  const showGenericError = () => {
    showAlertToast(t('OpeningHoursEditModal.unexpected_error.title'));
  };

  const isSubmitDisabled = !formState.isValid || isUpdating;

  useEffect(() => {
    if (userProfileErrors) {
      Logger.error(`OpeningHoursEditModal: ${JSON.stringify(userProfileErrors)}`);
      showGenericError();
      dispatch.errors.clear();
    }
  }, [userProfileErrors]);

  const handleSubmit = async (formData, event) => {
    try {
      await editContactPage({
        openingHours: {
          additionalInformation: formData.additionalInformation,
          slots: {
            monday: formData.slots.monday.filter(slot => slot !== ''),
            tuesday: formData.slots.tuesday.filter(slot => slot !== ''),
            wednesday: formData.slots.wednesday.filter(slot => slot !== ''),
            thursday: formData.slots.thursday.filter(slot => slot !== ''),
            friday: formData.slots.friday.filter(slot => slot !== ''),
            saturday: formData.slots.saturday.filter(slot => slot !== ''),
            sunday: formData.slots.sunday.filter(slot => slot !== ''),
          },
        },
      });
      trackEvent(event, { actionText: 'edit' });
      close({ modal: MODALS.openingHours });
      showSuccessToast(t('OpeningHoursEditModal.success.title'));
    } catch (e) {
      e.message = `OpeningHoursEditModal#handleSubmit(): ${e.message}`;
      Logger.error(e);
      showGenericError();
    }
  };

  const handleClose = e => {
    e.preventDefault();
    close({ modal: MODALS.openingHours });
  };

  return (
    <Modal
      showCloseButton
      className="min-h-0 flex flex-col"
      onClickOutside={handleClose}
      onClickCloseButton={handleClose}
      title={t('OpeningHoursEditModal.title')}
    >
      <div data-component="OpeningHoursEditModal" className="space-y-8">
        <div className="text-slate-600">{t('OpeningHoursEditModal.subtitle')}</div>
        <form onSubmit={wrapSubmit(handleSubmit)} className="space-y-4">
          {Object.values(WEEKDAYS).map(day => (
            <div className="grid grid-cols-5 gap-4 items-center">
              <label className="font-semibold block col-span-1" htmlFor={`slots.${day}`}>
                {t(`OpeningHoursEditModal.form.${day}`)}
              </label>
              <InputField
                ref={registerFormElement}
                className="col-span-2"
                name={`slots.${day}[0]`}
                defaultValue={slots?.[day]?.[0]}
                placeholder="HH:MM - HH:MM"
                maxLength="30"
              />
              <InputField
                ref={registerFormElement}
                className="col-span-2"
                name={`slots.${day}[1]`}
                defaultValue={slots?.[day]?.[1]}
                placeholder="HH:MM - HH:MM"
                maxLength="30"
              />
            </div>
          ))}
          <div className="space-y-2">
            <label className="font-semibold block" htmlFor="additionalInformation">
              {t('OpeningHoursEditModal.form.additionalInformation')} ({information?.length}/250)
            </label>
            <InputTextarea
              ref={registerFormElement}
              name="additionalInformation"
              defaultValue={additionalInformation}
              placeholder={t('OpeningHoursEditModal.form.additionalInformationPlaceholder')}
              onChange={value => setInformation(value)}
            />
          </div>
          <div className="mt-6 flex w-full justify-end">
            <Button type="submit" isDisabled={isSubmitDisabled}>
              {isUpdating ? <Spinner /> : t('OpeningHoursEditModal.form.save')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default OpeningHoursEditModal;
